import { useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import queryString from 'query-string'
import { actions as historyActions } from 'src/features/fw-ai/children/history/HistorySlice'
import { actions as chatActions } from 'src/features/fw-ai/children/dougallChat/ChatSlice'
import { actions as chatPageActions } from 'src/features/fw-ai/children/chatPage/ChatPageSlice'
import useChat from './useChat'

function decodeHtml(html) {
  let txt = document.createElement('textarea')
  txt.innerHTML = html
  const textValue = txt.value
  txt.remove()
  return textValue
}

export const prepromptRef = { current: null }

/**
 * Hook to handle all URL related prompts
 */
const useUrlPrompts = () => {
  // Hooks
  const dispatch = useDispatch()
  const { isFetching, promptRequest, setPromptNum } = useChat()
  const { t } = useTranslation()

  // Url Parameters
  const {
    source = undefined,
    prompt = undefined,
    promptNum = undefined,
    dspId = undefined,
    pId = undefined,
    articleId = undefined,
    type = undefined,
    preprompt = undefined
  } = queryString.parse(window.location.search)
  /**
   * URL prompt params action
   */
  useEffect(() => {
    // Prompt from UBQ3 ads
    // expects url params: dspId, prompt, promptNum
    if (dspId && prompt && promptNum && !isFetching) {
      promptRequest({
        hcpPrompt: decodeHtml(prompt),
        articleId,
        type: 'ubq3'
      })
      setPromptNum(promptNum)
    }

    // DougallApps prompt handling
    // expects url params: articleId, type
    else if (articleId && type) {
      // Get translated prompt to show
      let dougallAppPrompt
      switch (type) {
        case 'implications':
          dougallAppPrompt = t('dougallAppsPromptImplications')
          break
        case 'explain':
          dougallAppPrompt = t('dougallAppsPromptExplain')
          break
        default:
          dougallAppPrompt = decodeHtml(prompt)
          break
      }
      // Submit prompt
      promptRequest({
        hcpPrompt: dougallAppPrompt,
        articleId,
        type
      })
    }

    // Prompt submitted in other site or NL
    // expects url params: prompt, source, articleId(optional)
    else if (prompt && source && !isFetching) {
      promptRequest({
        hcpPrompt: decodeHtml(prompt),
        articleId,
        type: source === 'DGPTLessons' ? 'lessons' : 'regular'
      })
    }
    // eslint-disable-next-line
  }, [prompt, promptNum])

  // Shared Prompts
  useEffect(() => {
    if (pId && source) {
      dispatch(historyActions.requestPromptHistory({ pId, source }))
    }
    // eslint-disable-next-line
  }, [pId, source])
  // Pre prompt (will populate prompt bar only)
  useEffect(() => {
    if (preprompt) {
      prepromptRef.current = preprompt
      dispatch(
        chatActions.setFormState({
          prompt: preprompt
        })
      )
      dispatch(chatPageActions.displayContainer({ container: 'chat' }))
    }
  }, [preprompt])
  // Clean up URL query parameters once used
  useEffect(() => {
    // Add additional URL parameters to 'usedParams' if you want them to be cleared from the URL after use, e.g. [preprompt, prompt, promptNum]
    const usedParams = [preprompt]
    const anyUsed = usedParams.some(p => p !== undefined)

    if (anyUsed) {
      const newUrl = window.location.pathname + window.location.hash
      window.history.replaceState({}, document.title, newUrl)
    }
  }, [preprompt])
}

export default useUrlPrompts
